/** The attachments strip every composer shares: Attach files (browser) · Attach from file (customer file / application /
 *  the email being replied to) · chips with sizes · upload progress. `items` follow components/mail/attachments.js. */
import { useEffect, useRef, useState } from "react";
import { FolderOpen, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { AttachFromFileDialog } from "./AttachFromFileDialog";
import { fmtSize, itemSize, mergeItems, totalBytes, uploadItem } from "./attachments";

const ORIGIN_DOT = { OneDrive: "bg-sky-500", LoanDisk: "bg-emerald-500", Monday: "bg-violet-500", DocuPilot: "bg-amber-500", DocuSign: "bg-indigo-500", Email: "bg-slate-500" };

export function AttachmentsBox({ items, onChange, disabled, fileSource, defaultScope, onScopePicked, messageAttachments, progress, testPrefix = "compose" }) {
  const fileRef = useRef(null);
  const [picker, setPicker] = useState(false);
  const [scope, setScope] = useState(defaultScope || null);
  const scopeKey = defaultScope ? JSON.stringify(defaultScope) : "";
  useEffect(() => { if (scopeKey) setScope(JSON.parse(scopeKey)); }, [scopeKey]);
  const total = totalBytes(items);
  const add = (incoming) => {
    const next = mergeItems(items, incoming);
    if (!next) { toast.error("Attachments must total under 25 MB"); return; }
    onChange(next);
  };
  const addFiles = (list) => add(Array.from(list || []).map(uploadItem));
  return (
    <div className="border rounded p-2 bg-slate-50/60" data-testid={`${testPrefix}-attachments`} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}>
      <div className="flex items-center gap-2 flex-wrap">
        <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => fileRef.current?.click()} disabled={disabled} data-testid={`${testPrefix}-attach-btn`}>
          <Paperclip className="w-3 h-3 mr-1" />Attach files
        </Button>
        <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid={`${testPrefix}-file-input`} />
        {fileSource && (
          <Button type="button" size="sm" variant="outline" className="h-7 text-[11px] border-indigo-300 text-indigo-800 hover:bg-indigo-50" onClick={() => setPicker(true)} disabled={disabled} data-testid={`${testPrefix}-attach-from-file-btn`}>
            <FolderOpen className="w-3 h-3 mr-1" />Attach from file{scope ? ` · ${fileSource.short(scope)}` : "…"}
          </Button>
        )}
        <span className="text-[10px] text-muted-foreground">{items.length ? `${items.length} file${items.length === 1 ? "" : "s"} · ${fmtSize(total)}` : "or drop files on this dialog — up to 25 MB in total"}</span>
      </div>
      {items.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {items.map((it, i) => (
            <li key={it.id} className="inline-flex items-center gap-1 border rounded bg-white px-2 py-0.5 text-[11px] max-w-[280px]" title={it.kind === "pick" ? `${it.origin} · ${it.name}` : it.file.name} data-testid={`${testPrefix}-attachment-${i}`}>
              {it.kind === "pick" ? <span className={cn("w-2 h-2 rounded-full shrink-0", ORIGIN_DOT[it.origin] || "bg-slate-400")} /> : <Paperclip className="w-3 h-3 text-slate-400 shrink-0" />}
              <span className="truncate">{it.kind === "pick" ? it.name : it.file.name}</span>
              <span className="text-slate-400 shrink-0">{it.kind === "pick" && it.size == null ? "size ?" : fmtSize(itemSize(it))}</span>
              <button type="button" className="text-slate-400 hover:text-red-600 shrink-0" onClick={() => onChange(items.filter((_, j) => j !== i))} disabled={disabled} data-testid={`${testPrefix}-attachment-remove-${i}`}><X className="w-3 h-3" /></button>
            </li>
          ))}
        </ul>
      )}
      {progress && <div className="mt-2 text-[10px] text-blue-700" data-testid={`${testPrefix}-upload-progress`}>Uploading {progress.name} ({progress.i}/{progress.n}) — {progress.pct}%</div>}
      {picker && <AttachFromFileDialog fileSource={fileSource} defaultScope={scope} onScopeChange={(s) => { setScope(s); onScopePicked?.(s); }} messageAttachments={messageAttachments} alreadyBytes={total} existing={items} onAdd={add} onClose={() => setPicker(false)} />}
    </div>
  );
}
